import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, TrendingUp, Calendar, Trophy, Sun, Moon, Sunset } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAvas } from '../../hooks/useDashboardData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HeroSection = ({ onCustomize }) => {
  const { user } = useAuth();
  const { avas } = useAvas();
  const navigate = useNavigate();
  const [schoolName, setSchoolName] = useState(null);

  useEffect(() => {
    if (!user?.school_id) return;
    fetch(`${API_URL}/api/school/branding/${user.school_id}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setSchoolName(data.school_name); })
      .catch(() => {});
  }, [user?.school_id]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const getTimeIcon = () => {
    const hour = new Date().getHours();
    if (hour < 12) return <Sun size={22} className="text-amber-500" />;
    if (hour < 18) return <Sunset size={22} className="text-orange-400" />;
    return <Moon size={22} className="text-indigo-400" />;
  };

  const getPersonalizedMessage = () => {
    if (user?.current_streak >= 7) {
      return `You're on a ${user.current_streak}-day streak! Keep it going!`;
    }
    if (user?.last_active) {
      return "Ready for today's session?";
    }
    return "Ready for today's session?";
  };

  const getPrimaryAvaName = () => {
    if (user?.primary_ava_name) return user.primary_ava_name;
    const primaryAva = avas?.find(a => a.is_primary);
    if (primaryAva) return primaryAva.name;
    if (avas?.length > 0) return avas[0].name;
    return 'your AvA';
  };

  return (
    <section className="hero-section animate-fadeIn" data-testid="hero-section">
      <div className="hero-content">
        <div className="text-center">
          <p className="text-sm font-medium text-gray-500 mb-3 tracking-wide" data-testid="mission-prompt">
            HiBFF is here to help you need us less.
          </p>
          <h1 className="hero-greeting flex items-center justify-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-2">
              {getTimeIcon()}
              {getGreeting()},
            </span>
            <span>{user?.name?.split(' ')[0] || 'there'}!</span>
          </h1>
          <p className="hero-subtext">
            {getPersonalizedMessage()}
          </p>
          {schoolName && (
            <p className="mt-1" style={{ fontSize: 12, color: '#999' }} data-testid="hero-via-school">
              Via {schoolName}
            </p>
          )}
        </div>
      </div>

      <div className="quick-actions" style={{ justifyItems: 'stretch' }}>
        <button
          onClick={() => navigate('/conversations')}
          className="quick-action-button quick-action-maya"
          data-testid="chat-action"
        >
          <MessageCircle size={18} />
          <span>Practice with {getPrimaryAvaName()}</span>
        </button>
        <button
          onClick={() => navigate('/challenges')}
          className="quick-action-button quick-action-challenge"
          data-testid="checkin-action"
        >
          <Calendar size={18} />
          <span>Today&apos;s Challenge</span>
        </button>
        <button
          onClick={() => navigate('/achievements')}
          className="quick-action-button quick-action-progress"
          data-testid="badges-action"
        >
          <Trophy size={18} />
          <span>My Progress</span>
        </button>
        <button
          onClick={() => navigate('/badges')}
          className="quick-action-button quick-action-goals"
          data-testid="wellness-action"
        >
          <TrendingUp size={18} />
          <span>Real-World Goals</span>
        </button>
      </div>
    </section>
  );
};

export default HeroSection;
